<template>
  <container
    v-show="isTableReady"
    id="printable"
    :class="[{ [$style.message]: isTableAwaiting }, $style.scrollableContent]"
  >
    <div :class="$style.tableTitle">
      <div>{{ data.title }}</div>
      <div :class="$style.search">
        <label>
          <input
            v-model="filter"
            :class="$style.input"
            type="text"
            :placeholder="$t('reports.filterByWord')"
          >
        </label>
      </div>
    </div>
    <table :class="$style.table">
      <div :class="$style.wrapper">
        <thead :class="[$style.header, { [$style.marginScroll]: isScroll }]">
          <th
            v-for="column in columns"
            :key="column.name"
            :style="{ flex: column.flex }"
            @click="changeSort(column)"
          >
            <div :class="$style.tdInline">
              {{ column.name }}
            </div>
            <img
              src="@/assets/images/icons/table/sort-arrow.svg"
              :class="[
                $style.tdInline,
                $style.arrow,
                {
                  [$style.asc]: column.sort == 2,
                  [$style.hidden]: column.sort == 0,
                },
              ]"
            >
          </th>
        </thead>
      </div>
      <tbody :class="$style.tBody">
        <virtual-list
          ref="virtualList"
          :size="40"
          :remain="remain"
          :keeps="20"
        >
          <tr
            v-for="user in displayedTable"
            :key="user.name"
            :class="$style.item"
          >
            <td
              v-for="column in columns"
              :key="column.name + user.name"
              :style="{ flex: column.flex }"
              :class="[{ [$style.tdBody]: isTimline(column.field) }]"
            >
              <div
                v-if="isTimline(column.field)"
                :class="$style.timeline"
              >
                <td>{{ user[column.field] }}</td>
                <td :class="$style.tdTimline">
                  <svg :class="$style.svg">
                    <g>
                      <rect
                        :width="getWidth(user[column.field])"
                        :x="'0%'"
                        :class="$style.bar"
                      />
                    </g>
                  </svg>
                </td>
              </div>

              <div v-else>
                {{ user[column.field] }}
              </div>
            </td>
          </tr>
        </virtual-list>
      </tbody>
    </table>

    <div
      v-if="data.data.length"
      :class="$style.chartContainer"
    >
      <div :class="$style.chartWrapper">
        <pie-chart
          :id="`pieChartData_${index}`"
          :class="$style.chart"
          :data="pieChartData"
        />
      </div>
      <div :class="$style.chartWrapper">
        <pie-chart
          :id="`pieChartAttitude_${index}`"
          :class="$style.chart"
          :data="pieChartAttitude"
        />
      </div>
    </div>
    <div
      v-else
      :class="$style.emptyResult"
    >
      {{ $t('global.noData') }}
    </div>
  </container>
</template>

<script>
  import Container from '@/components/common/blocks/container.vue';
  import { ROUTE_NAMES_FILES } from '@/views/reports/const/const';
  import { LogData } from '@/domains/log/data';
  import { getDeepCopy } from '@/utils/deep-copy';
  import virtualList from 'vue-virtual-scroll-list';
  import { REPORT_ENUM } from '@/assets/scripts/reportEnum';
  import PieChart from './pie-chart.vue';

  export default {
    components: {
      Container,
      PieChart,
      virtualList,
    },
    props: ['data', 'index'],
    data: () => ({
      filter: '',
      tableData: [],
      pieChartData: {},
      pieChartAttitude: {},
    }),
    computed: {
      isTableReady() {
        return this.$store.getters['report/getStateTable'] === REPORT_ENUM.TABLE_READY;
      },
      isTableAwaiting() {
        return this.$store.getters['report/getStateTable'] === REPORT_ENUM.REQUEST_AWAITING;
      },
      isScroll() {
        return this.displayedTable.length >= 10;
      },
      remain() {
        if (!this.isScroll) return this.displayedTable.length;
        return 10;
      },
      filters() {
        return this.columns.map((item) => item.field);
      },
      displayedTable() {
        return this.data.data;
      },
      columns() {
        return this.data.columns;
      },
    },

    watch: {
      filter(newVal, oldVal) {
        if (newVal !== oldVal) {
          this.data.data = this.tableData;
          this.pieChartData = getDeepCopy(this.data.pieChartData);
          this.pieChartAttitude = getDeepCopy(this.data.pieChartAttitude);
          if (newVal) {
            this.getTableData();
            this.getPieChartData();
          }
          this.$refs.virtualList.forceRender();
        }
      },
    },
    mounted() {
      this.updateAndSortData();
      this.tableData = this.data.data;
      this.pieChartData = getDeepCopy(this.data.pieChartData);
      this.pieChartAttitude = getDeepCopy(this.data.pieChartAttitude);
    },

    methods: {
      getTableData() {
        let items = [...this.data.data];
        items = items.filter((element) => element !== undefined);
        if (items.length) {
          this.data.data = LogData.filterTable(items, this.filters, this.filter);
        }
      },
      getPieChartData() {
        this.pieChartData.labels = [];
        this.pieChartData.data = [];
        this.pieChartAttitude.data = [];
        this.pieChartAttitude.labels = [];

        if (this.data.type === ROUTE_NAMES_FILES.EXTERNAL_DRIVES) {
          this.data.data.forEach(({ nameUser, countExternal }) => {
            if (countExternal) {
              this.pieChartData.labels.push(nameUser);
              this.pieChartData.data.push(countExternal);
            }
          });
        } else {
          const arrNames = this.data.data.map(({ username }) => username);
          const uniqNames = new Set(arrNames);
          uniqNames.forEach((name) => {
            const filteredData = this.data.data.filter(({ username }) => username === name);
            const fullSize = filteredData.reduce((acc, item) => acc + item.filesize, 0);
            if (filteredData) {
              this.pieChartData.data.push(filteredData.length);
              this.pieChartData.labels.push(name);
              this.pieChartAttitude.data.push(fullSize);
              this.pieChartAttitude.labels.push(name);
            }
          });
        }
      },
      isTimline(field) {
        return field === 'countExternal' || field === 'filesize';
      },
      getWidth(value) {
        if (value) {
          return `${(value / this.data.maxWidth) * 100}%`;
        }
        return '0%';
      },
      updateAndSortData() {
        this.data.data.sort(this.compare);
      },
      compare(a, b) {
        const index = this.columns.findIndex((x) => x.sort > 0);
        const key = this.columns[index].field;

        a = a[key];
        b = b[key];

        if (typeof a === 'string' && typeof b === 'string') {
          a = a.toLowerCase();
          b = b.toLowerCase();
        }

        if (this.columns[index].sort === 2) {
          if (a > b) {
            return -1;
          }

          if (a < b) {
            return 1;
          }
          return 0;
        }

        if (this.columns[index].sort === 1) {
          if (a < b) {
            return -1;
          }

          if (a > b) {
            return 1;
          }

          return 0;
        }
        return 0;
      },
      changeSort(column) {
        const { sort } = column;

        this.columns.forEach((col) => {
          col.sort = 0;
        });

        if (sort === 1) {
          column.sort = 2;
        } else column.sort = 1;
        this.updateAndSortData();
        this.$emit('setData');
      },
    },
  };
</script>

<style lang="scss" module>
  ::-webkit-scrollbar {
    width: 4px;
  }

  ::-webkit-scrollbar-track {
    background: white;
  }

  ::-webkit-scrollbar-thumb {
    background: $branchColor;
    border-radius: 2px;
  }

  ::-webkit-scrollbar-thumb:hover {
    background: darkgray;
  }

  .scrollableContent {
    margin: 0 8px 0 0;
    padding: 0 12px 0 20px;
    box-shadow: $shadow;
    flex: 1;
  }

  .message {
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .table {
    width: 100%;
    margin-bottom: 40px;
    border-collapse: collapse;
    font-weight: 400;
    word-break: break-word;
    border: 1px solid $borderColor;
  }
  .wrapper {
    background: #f5f5f5;
    border-bottom: 1px solid $borderColor;
  }
  .header {
    background: #f5f5f5;
    height: 38px;
    position: sticky;
    z-index: 3;
    display: flex;
    th {
      border-right: 1px solid $borderColor;
      border-left: 1px solid $borderColor;
      align-items: center;
      text-align: center;
      color: #232326;
      letter-spacing: -0.08px;
      font-size: 13px;
      padding: 10px 4px 10px 10px;
      cursor: pointer;
      &:last-child {
        border-right: 1px solid transparent;
      }
    }
  }
  .marginScroll {
    margin-right: 4px;
  }
  .tBody {
    display: inline-table;
    width: 100%;
  }
  .item {
    display: flex;
    height: 40px;
    cursor: pointer;
    border-bottom: 0.2px solid #eee;

    &:hover {
      background: $light-gray;
    }

    td {
      border-right: 1px solid $borderColor;
      border-left: 1px solid $borderColor;
      align-items: center;
      letter-spacing: -0.24px;
      color: $black;
      font-weight: 300;
      font-size: 12px;
      line-height: 14px;
      text-align: center;
      font-size: 14px;
      letter-spacing: 0px;
      text-align: left;
      padding: 7px 4px 4px 10px;
    }
  }
  td {
    align-content: center;
  }
  .tdInline {
    display: inline-flex;
  }

  .timeline {
    display: flex;
    gap: 10px;
    td {
      padding: 0;
      border: none;
    }
  }
  .tdBody {
    width: 450px;
  }
  .tdTimline {
    flex: 1;
  }

  .arrow {
    float: right;
    margin-top: 7px;
    margin-right: 5px;

    &.asc {
      transform: rotate(180deg);
    }

    &.hidden {
      visibility: hidden;
    }
  }
  .chartContainer {
    display: flex;
    gap: 60px;
    justify-content: center;
    height: 500px;
    break-inside: avoid;
    page-break-inside: avoid;
  }

  .chartWrapper {
    text-align: center;
    width: 400px;
    break-inside: avoid;
    page-break-inside: avoid;
  }
  .chart {
    width: 100%;
    height: 400px;
    break-inside: avoid;
    page-break-inside: avoid;
  }

  .svg {
    width: 100%;
    height: 23px;
    overflow: visible;
  }
  .bar {
    height: 23px;
    fill: #2985bf;
    flex: 1;
  }
  .tableTitle {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin: 20px;
    font-size: 18px;
  }

  .search {
    margin: 0 5px;
  }

  .input {
    border: 1px solid $borderColor;
    color: $black;
    font-size: 14px;
    line-height: 20px;
    padding: 6px 12px;

    &::placeholder {
      color: dakren($dark-gray, 3%);
    }
  }

  .emptyResult {
    display: flex;
    justify-content: center;
    margin-bottom: 20px;
  }
</style>
